import { noop } from 'lodash-es'
import { isNotNil } from './object-util'

/**
 * Calls {@link callback} when the signal aborts. If the signal is already
 * aborted, calls immediately.
 *
 * @param signal The signal. Can be nullish. If so, the callback is never called.
 * @param callback The callback.
 * @returns an unsubscribe function.
 */
export function onAbort(
  signal: AbortSignal | null | undefined,
  callback: () => unknown,
): () => void {
  if (!signal) {
    return noop
  }

  if (signal.aborted) {
    callback()
    return noop
  }

  const removeListener = () => signal.removeEventListener('abort', fn)

  const fn = () => {
    removeListener()
    callback()
  }

  signal.addEventListener('abort', fn)
  return removeListener
}

/**
 * Sentinel value used for operations that swallow abort errors
 * and need a return value.
 */
export const ABORTED: unique symbol = Symbol('Aborted')

/**
 * Resolves the promise with the {@link ABORTED} sentinel value
 * if the abort signal triggers.
 *
 * @param promise
 * @param signal
 */
export function resolveOnAbort<P extends Promise<unknown>>(
  promise: P,
  signal: AbortSignal | undefined,
): Promise<Awaited<P> | typeof ABORTED> {
  return new Promise<Awaited<P> | typeof ABORTED>((resolve, reject) => {
    const unsubscribe = onAbort(signal, () => resolve(ABORTED))
    promise.then(
      (value) => {
        unsubscribe()
        resolve(value as Awaited<P>)
      },
      (err: Error) => {
        unsubscribe()
        reject(err)
      },
    )
  })
}

/**
 * Throws if the {@link value} is the {@link ABORTED} sentinel value.
 * @param value
 */
export function throwIfAborted<T>(
  value: T,
): T extends typeof ABORTED ? Exclude<T, typeof ABORTED> : T {
  if (value === ABORTED) {
    throw createAbortError()
  }

  // @ts-ignore: yes
  return value
}

/**
 * Checks if {@link error} is an abort error.
 *
 * @param error
 */
export function isAbortError(
  error: unknown,
): error is typeof ABORTED | DOMException {
  return (
    error === ABORTED ||
    (error instanceof DOMException && error.name === 'AbortError')
  )
}

/**
 * Creates an abort error.
 *
 * @param message
 */
export function createAbortError(message?: string) {
  return new DOMException(message, 'AbortError')
}

/**
 * Returns a function that can be passed to a `.catch` of a Promise
 * that ignores {@link DOMException}s with `name` of `AbortError` as well as
 * the {@link ABORTED} sentinel
 * value instead.
 */
export function ignoreAbortError(): (err: unknown) => typeof ABORTED | never {
  return (err) => {
    if (isAbortError(err)) {
      return ABORTED
    }
    throw err
  }
}

/**
 * Returns an abort signal that aborts when any of the given signals abort.
 *
 * @param signals
 */
export function anyAbortSignal(
  ...signals: (AbortSignal | undefined | null)[]
): AbortSignal {
  return AbortSignal.any(signals.filter(isNotNil))
}

/**
 * An abort controller that exposes an {@link AbortSignal} that only aborts once
 * all the participating abort signals have aborted.
 */
export class UnanimousAbortController {
  /**
   * Whether the controller has been disposed.
   * @private
   */
  private _disposed = false

  /**
   * Signals participating in voting to abort.
   *
   * @private
   */
  private _signals = new Set<AbortSignal>()

  /**
   * Controls the unanimous signal.
   *
   * @private
   */
  private _inner = new AbortController()

  /**
   * The signal that aborts when unanimously agreed upon by
   * all participating signals.
   */
  readonly signal = this._inner.signal

  /**
   * Adds a signal.
   *
   * @param signal
   */
  add(signal: AbortSignal) {
    if (this._disposed) {
      return
    }

    signal.addEventListener('abort', this._onParticipatingSignalAbort)
    this._signals.add(signal)
    this._maybeAbort()
  }

  /**
   * Removes a signal that was added via {@link add}.
   *
   * @param signal
   */
  remove(signal: AbortSignal) {
    signal.removeEventListener('abort', this._onParticipatingSignalAbort)
    this._signals.delete(signal)
  }

  /**
   * Disposes the controller; no new signals can be registered afterwards.
   * Removes all currently tracked signals.
   */
  dispose() {
    if (this._disposed) {
      return
    }

    this._disposed = true
    for (const signal of this._signals) {
      this.remove(signal)
    }
  }

  /**
   * Event handler which is registered on each signal tracked.
   */
  private _onParticipatingSignalAbort = () => {
    this._maybeAbort()
  }

  /**
   * Aborts if all currently tracked signals have aborted.
   *
   * @private
   */
  private _maybeAbort() {
    if (this._inner.signal.aborted) {
      return
    }

    if (this._signals.size === 0) {
      return
    }

    let reason: unknown = undefined
    for (const signal of this._signals) {
      if (!signal.aborted) {
        return
      }

      reason = signal.reason
    }

    // All signals aborted - trigger abort and cleanup.
    this._inner.abort(reason)
    this.dispose()
  }
}
